import { useRef, useState, type FormEvent } from 'react';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  Form,
  Heading,
  Paragraph,
  Stack,
  TextArea,
  TextField,
  useConfirmationModal,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import type { PlanType } from '@/services/billing/plans';
import { isEmptyString } from '@/utils/string';
import { sentryCaptureException } from '@/utils/sentry';
import { useZodForm, type UseZodFormReturn } from '@/components/form';
import { contractsSalesGreeting } from '../utils/greeting';
import {
  ContractsSalesSchema,
  type ContractsSalesInput,
  type ContractsSalesOutput,
} from '../utils/contracts-sales-schema';
import { useContractsSalesAnalytics } from '../hooks/use-contracts-sales-analytics';
import { useSubmitContractsSales } from '../hooks/use-submit-contracts-sales';

const FORM_ID = 'contracts-sales-form';

type FieldName = keyof ContractsSalesInput;
type RegisterField = (name: FieldName) => ReturnType<UseZodFormReturn<ContractsSalesInput>['registerPluma']>;

type ContractsSalesDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The plan whose "Let's talk" button was clicked; tailors the greeting. */
  planClicked?: PlanType;
};

type ContractsSalesFieldsProps = {
  registerField: RegisterField;
  firstName: string;
  greeting: string;
  onSubmit: (event: FormEvent<Element>) => void | Promise<void>;
};

/** The contracts-sales request form: a greeting plus the message, features, and reply-to fields. */
function ContractsSalesFields({ registerField, firstName, greeting, onSubmit }: ContractsSalesFieldsProps) {
  return (
    <Form id={FORM_ID} onSubmit={onSubmit}>
      <Stack gap="300">
        <Paragraph data-test-inline-contracts-sales-welcome-message>
          👋 Hi {firstName}, {greeting}
        </Paragraph>
        <TextArea
          label="How can we help you?"
          placeholder="e.g. I'm interested in upgrading to your Premium Journeys plan with Data Pipelines"
          minRows={5}
          data-test-inline-contracts-sales-input-message
          {...registerField('message')}
        />
        <TextArea
          label="Are you interested in any specific features?"
          placeholder="e.g. I'm curious about custom objects and integrations"
          minRows={5}
          data-test-inline-contracts-sales-features-message
          {...registerField('features')}
        />
        <TextField
          label="We will send replies to this email address"
          data-test-inline-contracts-sales-input-reply-to
          {...registerField('replyTo')}
        />
      </Stack>
    </Form>
  );
}

/** The confirmation screen shown after a request is submitted. */
function ThankYouBody() {
  return (
    <Stack gap="200" textAlign="center" pt="1200" data-test-inline-contracts-sales-thank-you-page>
      <Heading level="3">Thanks for reaching out</Heading>
      <Paragraph>
        We will get back to you as soon as we can. We appreciate your loyalty as a customer and we're excited to help
        you grow with Customer.io.
      </Paragraph>
    </Stack>
  );
}

// Owns the contracts-sales form: RHF setup, the submitted flag, the greeting, analytics, and the
// submit/cancel/close guards. Keeping it out of the component leaves the drawer a flat render.
function useContractsSalesDrawer({ isOpen, onClose, planClicked }: ContractsSalesDrawerProps) {
  const { user } = useAuth();
  const isBuilder = useStore(billing.isBuilder);
  const isTrial = useStore(billing.isTrial);
  const planType = useStore(billing.planType);

  const [submitted, setSubmitted] = useState(false);
  const submit = useSubmitContractsSales();
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();

  const form = useZodForm({
    schema: ContractsSalesSchema,
    defaultValues: { message: '', features: '', replyTo: user.email },
  });
  const analytics = useContractsSalesAnalytics({ isOpen, planClicked });

  const firstName = isEmptyString(user.first_name) ? 'there' : user.first_name;
  const greeting = contractsSalesGreeting({ isBuilder, isTrial, planType, planClicked });

  // Wrap each field's onInput to fire the "field input" analytics the first time it changes
  // (event-driven, so no `form.watch`).
  const trackedFields = useRef<Set<string>>(new Set());
  const registerField: RegisterField = (name) => {
    const fieldProps = form.registerPluma(name);
    return {
      ...fieldProps,
      onInput: (event) => {
        fieldProps.onInput?.(event);
        if (!trackedFields.current.has(name)) {
          trackedFields.current.add(name);
          analytics.trackFieldInput(name);
        }
      },
    };
  };

  const submitValid = form.handleSubmit(async (values: ContractsSalesOutput) => {
    try {
      await submit.mutateAsync(values);
      analytics.trackSubmitSuccess();
      setSubmitted(true);
    } catch (error) {
      sentryCaptureException(error);
      analytics.trackSubmitError();
      snackbars.create({
        variant: 'critical',
        id: 'contracts-sales.submit.error',
        message: "Your request couldn't be sent. Try again.",
      });
    }
  });

  // Fire "submit clicked" on every submit attempt (before validation), then let RHF validate.
  // Return the RHF submit promise so Pluma's Form autoLoading locks the fields while it's pending
  // (double-submit protection); dropping it lets an Enter keypress re-fire the request mid-flight.
  const handleSubmit = (event: FormEvent<Element>) => {
    analytics.trackSubmitClicked();
    return submitValid(event);
  };

  // Cancel button: warn before discarding a partially-filled form.
  const handleCancel = async () => {
    analytics.trackCancelClicked();
    const { message, features } = form.getValues();
    const hasUserInput = message.trim().length > 0 || features.trim().length > 0;

    if (!hasUserInput) {
      onClose();
      return;
    }

    const confirmed = await confirm({
      title: 'Discard this request?',
      message: 'Your progress will be lost.',
      confirmLabel: 'Discard request',
      isCritical: true,
      cancelLabel: 'Cancel',
    });

    if (confirmed) {
      onClose();
    } else {
      analytics.trackCancelDismissed();
    }
  };

  // Reset the form and success state once the close animation finishes, so reopening starts fresh.
  const handleCloseComplete = () => {
    setSubmitted(false);
    trackedFields.current = new Set();
    form.reset({ message: '', features: '', replyTo: user.email });
  };

  return {
    registerField,
    firstName,
    greeting,
    submitted,
    submit,
    handleSubmit,
    handleCancel,
    handleCloseComplete,
    isOpen,
    onClose,
  };
}

/**
 * The "Let's talk" contracts-sales sidebar: a request form that posts to the plan-appropriate support
 * inbox, then shows a thank-you confirmation.
 */
export function ContractsSalesDrawer(props: ContractsSalesDrawerProps) {
  const {
    registerField,
    firstName,
    greeting,
    submitted,
    submit,
    handleSubmit,
    handleCancel,
    handleCloseComplete,
    isOpen,
    onClose,
  } = useContractsSalesDrawer(props);

  return (
    <Drawer
      title="Contact us"
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={handleCloseComplete}
      size="md"
      data-test-inline-contracts-sales
    >
      <DrawerBody>
        {submitted ? (
          <ThankYouBody />
        ) : (
          <ContractsSalesFields
            registerField={registerField}
            firstName={firstName}
            greeting={greeting}
            onSubmit={handleSubmit}
          />
        )}
      </DrawerBody>
      <DrawerFooter>
        {submitted ? (
          <Button variant="secondary" onClick={onClose} data-test-inline-contracts-sales-thank-you-close>
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="tertiary"
              onClick={() => void handleCancel()}
              data-test-inline-contracts-sales-navigation-cancel
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              variant="primary"
              icon="send-auto"
              iconPosition="trailing"
              isLoading={submit.isPending}
              data-test-inline-contracts-sales-navigation-submit
            >
              Send request
            </Button>
          </>
        )}
      </DrawerFooter>
    </Drawer>
  );
}
